import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Link,
  Card,
  CardBody,
  Flex,
  Grid,
  GridItem,
  Spinner,
  Icon,
  Divider,
} from "@chakra-ui/react";
import {
  IoLocationOutline,
  IoCallOutline,
  IoGlobeOutline,
  IoSchoolOutline,
  IoChevronForwardOutline,
  IoTimeOutline,
} from "react-icons/io5";
import InstitucionDetailMap from "../../components/InstitucionDetailMap";
import { Institucion } from "../../services/models/Institucion";
import ApiResponse from "../../services/ApiResponse";
import { useLocation, useNavigate } from "react-router-dom";
import { Carrera } from "../../services/models/Carrera";
import UnauthorizedPage from "../UnauthorizedPage";
import ServerErrorPage from "../ServerErrorPage";

export default function InstitucionDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const institucion = location.state as Institucion;
  const [response, setResponse] = useState(new ApiResponse<Carrera[]>());

  useEffect(() => {
    if (!institucion) {
      navigate("/badrequest", { replace: true });
      return;
    }

    const fetchCarreras = async () => {
      const apiResponse = new ApiResponse<Carrera[]>();
      await apiResponse.useFetch(
        `carreras/institucion/${institucion.id}`,
        "GET"
      );
      setResponse(apiResponse);
    };
    fetchCarreras();
  }, [institucion, navigate]);

  // Estilos constantes Senior (Glassmorphism para coincidir con tu Home)
  const glassStyle = {
    bg: "whiteAlpha.100",
    backdropFilter: "blur(10px)",
    border: "1px solid",
    borderColor: "whiteAlpha.200",
    borderRadius: "2xl",
    color: "white",
  };

  // Renderizado Condicional Limpio
  const renderContent = () => {
    if (response.loading) {
      return (
        <Flex justify="center" align="center" py={20}>
          <Spinner size="xl" thickness="4px" speed="0.65s" color="yellow.400" />
        </Flex>
      );
    }

    if (response.responseCode === 401) return <UnauthorizedPage />;
    if (response.responseCode !== 200 && !response.loading) return <ServerErrorPage />;

    if (response.data?.length === 0) {
      return (
        <VStack spacing={4} py={10} bg="whiteAlpha.50" borderRadius="xl" border="1px dashed" borderColor="whiteAlpha.300">
          <Icon as={IoSchoolOutline} boxSize={12} color="whiteAlpha.400" />
          <Text color="whiteAlpha.700">No hay carreras registradas actualmente.</Text>
        </VStack>
      );
    }

    return (
      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4}>
        {response.data?.map((carrera) => (
          <Box
            key={carrera.id}
            as="button"
            onClick={() => navigate(`/carreras/${carrera.id}`)}
            p={5}
            {...glassStyle}
            textAlign="left"
            transition="all 0.2s"
            _hover={{ bg: "whiteAlpha.200", borderColor: "yellow.400" }}
          >
            <Flex justify="space-between" align="center">
              <VStack align="start" spacing={2} flex={1}>
                <Badge colorScheme="orange" variant="solid" borderRadius="full" px={3}>
                  {carrera.tipo}
                </Badge>
                <Text fontSize="lg" fontWeight="bold" noOfLines={2}>
                  {carrera.nombre}
                </Text>
                <HStack color="whiteAlpha.600" fontSize="sm">
                  <Icon as={IoTimeOutline} />
                  <Text>
                    {carrera.duracion_anios > 0 && `${carrera.duracion_anios} años `}
                    {carrera.duracion_meses > 0 && `${carrera.duracion_meses} meses`}
                  </Text>
                </HStack>
              </VStack>
              <Icon as={IoChevronForwardOutline} boxSize={6} color="yellow.400" />
            </Flex>
          </Box>
        ))}
      </Grid>
    );
  };

  if (!institucion) return null;

  return (
    <Box bgGradient="linear(to-b, blue.800, blue.900)" minH="100vh" py={12} color="white">
      <Container maxW="container.xl">
        <VStack spacing={10} align="stretch">
          
          {/* Header de la Institución */}
          <VStack spacing={4} textAlign="center">
            <Heading size="2xl" letterSpacing="tight">
              {institucion.nombre}
            </Heading>
            <Divider borderColor="whiteAlpha.300" maxW="200px" />
          </VStack>

          {/* Info y Mapa */}
          <Grid templateColumns={{ base: "1fr", lg: "400px 1fr" }} gap={8}>
            <GridItem>
              <VStack spacing={6} align="stretch">
                <Box {...glassStyle} p={8}>
                  <VStack align="start" spacing={6}>
                    <InfoRow icon={IoLocationOutline} text={institucion.direccion} label="Dirección" />
                    <InfoRow icon={IoCallOutline} text={institucion.tel || "Sin teléfono"} label="Contacto" />
                    <InfoRow 
                      icon={IoGlobeOutline} 
                      text={institucion.pagina} 
                      label="Sitio Web" 
                      isLink 
                    />
                  </VStack>
                </Box>
              </VStack>
            </GridItem>

            <GridItem>
              <Box borderRadius="2xl" overflow="hidden" boxShadow="2xl" border="4px solid" borderColor="whiteAlpha.100" h="400px">
                <InstitucionDetailMap
                  nombre={institucion.nombre}
                  lati={institucion.ubicacion_lat || 0}
                  lngi={institucion.ubicacion_long || 0}
                />
              </Box>
            </GridItem>
          </Grid>

          {/* Listado de Carreras */}
          <Box mt={8}>
            <Heading size="xl" mb={8} color="yellow.400">
              Oferta Académica
            </Heading>
            {renderContent()}
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}

// Sub-componente para filas de información (Senior Clean Code)
const InfoRow = ({ icon, text, label, isLink }: any) => (
  <HStack spacing={4} align="start">
    <Flex bg="yellow.400" p={2} borderRadius="lg" color="blue.900">
      <Icon as={icon} boxSize={5} />
    </Flex>
    <VStack align="start" spacing={0}>
      <Text fontSize="xs" color="whiteAlpha.500" fontWeight="bold" textTransform="uppercase">
        {label}
      </Text>
      {isLink && text ? (
        <Link href={text} isExternal color="cyan.300" fontWeight="medium" noOfLines={1}>
          {text.replace(/^https?:\/\//, '')}
        </Link>
      ) : (
        <Text fontWeight="medium">{text || "N/A"}</Text>
      )}
    </VStack>
  </HStack>
);